import React, { useState } from "react";
import { Box, IconButton, Menu, MenuItem, ListItemIcon, ListItemText, useMediaQuery } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import UnarchiveOutlinedIcon from "@mui/icons-material/UnarchiveOutlined";
import { dt } from "../../../../shared/themes/redesignTokens";
import RedesignStatusBadge from "../../../../shared/components/redesign/RedesignStatusBadge";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { Offer, OfferStatusCode } from "../../models/models";
import { statusBadgeColors } from "../OfferCard";
import { createOfferStatusLabelMap } from "../../utils/offerStatusMaps";
import offersApi from "../../api/offersApi";
import OfferShareButtons from "./OfferShareButtons";
import dayjs from "dayjs";

interface OfferHeaderStripProps {
  offer: Offer;
  onDataRefresh: () => void;
}

const OfferHeaderStrip: React.FC<OfferHeaderStripProps> = ({ offer, onDataRefresh }) => {
  const { translate } = useTranslation(["serviceOffers", "common"]);
  const isPhone = useMediaQuery("(max-width:768px)");
  const statusLabelMap = createOfferStatusLabelMap(translate);

  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(menuAnchor);

  const canArchive =
    offer.statusCode === OfferStatusCode.serviceCompleted ||
    offer.statusCode === OfferStatusCode.rejected ||
    offer.statusCode === OfferStatusCode.expired ||
    offer.statusCode === OfferStatusCode.withdrawn;
  const canUnarchive = offer.statusCode === OfferStatusCode.archived;
  const hasAnyAction = canArchive || canUnarchive;

  const handleArchive = async () => {
    setMenuAnchor(null);
    await offersApi.archiveOffer(offer.id);
    onDataRefresh();
  };

  const handleUnarchive = async () => {
    setMenuAnchor(null);
    await offersApi.unarchiveOffer(offer.id);
    onDataRefresh();
  };

  const postedDate = offer.sentDate
    ? dayjs(offer.sentDate).format("MMM D, YYYY")
    : dayjs(offer.createdDate).format("MMM D, YYYY");

  const menuItemSx = {
    fontFamily: dt.font,
    fontSize: "14px",
    fontWeight: 500,
    color: dt.textPrimary,
    gap: 1.5,
    py: 1,
    px: 2,
    transition: "background-color 0.15s ease",
    "&:hover": { backgroundColor: dt.blueSoft },
    "& .MuiListItemIcon-root": { minWidth: "auto", color: dt.textSecondary },
  };

  return (
    <>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {/* Offer number + date + kebab */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Box
            sx={{
              fontFamily: dt.fontMono,
              fontSize: "13px",
              fontWeight: 700,
              color: dt.blue,
              letterSpacing: "0.3px",
            }}
          >
            {offer.internalNumber}
          </Box>
          <Box sx={{ fontSize: "13px", fontFamily: dt.fontMono, fontWeight: 400, color: dt.textMuted }}>|</Box>
          <Box sx={{ fontSize: "13px", fontFamily: dt.fontMono, fontWeight: 400, color: dt.textSecondary }}>
            {translate("serviceOffers:offerDetail.created")} {postedDate}
          </Box>

          {/* Share + Print + Kebab actions */}
          <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 0.5 }}>
            <OfferShareButtons offerNumber={offer.internalNumber} customerName={offer.customer?.name} />
            {hasAnyAction && (
              <IconButton
                onClick={(e) => setMenuAnchor(e.currentTarget)}
                size="small"
                sx={{
                  color: dt.textSecondary,
                  borderRadius: dt.radiusSm,
                  transition: "all 0.2s ease",
                  "&:hover": { backgroundColor: dt.blueGlow, color: dt.blue },
                }}
              >
                <MoreVertIcon sx={{ fontSize: 20 }} />
              </IconButton>
            )}
          </Box>
        </Box>

        {/* Status badge + customer + machine row */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <RedesignStatusBadge
            label={statusLabelMap[offer.statusCode] || ""}
            colorConfig={statusBadgeColors[offer.statusCode] || statusBadgeColors[OfferStatusCode.withdrawn]}
          />

          {/* Customer name inline */}
          {offer.customer?.name && (
            <Box
              sx={{
                fontSize: "13px",
                fontWeight: 600,
                fontFamily: dt.font,
                color: dt.textSecondary,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {translate("serviceOffers:offerDetail.forCustomer")} {offer.customer.name}
            </Box>
          )}

          {/* Machine name inline */}
          {offer.serviceRequest?.machine?.name && !isPhone && (
            <Box
              sx={{
                fontSize: "12px",
                fontFamily: dt.fontMono,
                fontWeight: 400,
                color: dt.textMuted,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              | {offer.serviceRequest.machine.name}
            </Box>
          )}
        </Box>
      </Box>

      {/* Kebab Menu */}
      {hasAnyAction && (
        <Menu
          anchorEl={menuAnchor}
          open={menuOpen}
          onClose={() => setMenuAnchor(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          slotProps={{
            paper: {
              sx: {
                borderRadius: "12px",
                boxShadow: "0 4px 20px rgba(0,0,0,0.08), 0 1px 4px rgba(0,0,0,0.04)",
                border: `1px solid ${dt.cardBorder}`,
                mt: 0.5,
                minWidth: 180,
                fontFamily: dt.font,
                "& *:not(.MuiSvgIcon-root)": { fontFamily: dt.font },
              },
            },
          }}
        >
          {canArchive && (
            <MenuItem onClick={handleArchive} sx={menuItemSx}>
              <ListItemIcon><ArchiveOutlinedIcon sx={{ fontSize: 18 }} /></ListItemIcon>
              <ListItemText>{translate("serviceOffers:card.archive")}</ListItemText>
            </MenuItem>
          )}
          {canUnarchive && (
            <MenuItem onClick={handleUnarchive} sx={menuItemSx}>
              <ListItemIcon><UnarchiveOutlinedIcon sx={{ fontSize: 18 }} /></ListItemIcon>
              <ListItemText>{translate("serviceOffers:card.unarchive")}</ListItemText>
            </MenuItem>
          )}
        </Menu>
      )}
    </>
  );
};

export default OfferHeaderStrip;
